import React, { useState, useEffect, useCallback, useRef } from "react";
import { ThemeContext } from "../../themes/ThemeContext";
import { getPayInStats, getPayInOutflows, getPayInInflows, transferToPayout, exportPayInInflows, exportPayInOutflows } from "../../data/api/api";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from "chart.js";
import { FaCreditCard } from "react-icons/fa";
import { BiTrendingDown, BiTrendingUp } from "react-icons/bi";
import NoData from "../../components/NoData/NoData";
import InflowVsOutFlowChart from "./components/InflowVsOutFlowChart";
import TableSection from "./components/TableSection";
import StatBox from "./components/StatBox";
import TransferToPayout from "./components/TransferToPayout";
import Loader from "../../components/Loader/Loader";
import { useTestPartner } from "../../hooks/useTestPartner";
import showToast from "../../utils/toastUtils";


// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const PayInDashboard = () => {
  const theme = React.useContext(ThemeContext);
  const { isTestPartner } = useTestPartner();
  const userFirstName = localStorage.getItem("userFirstName");
  const partnerId = localStorage.getItem("pid");

  const [accountStats, setAccountStats] = useState({
    balance: 0.0,
    outflow: 0.0,
    inflow: 0.0,
    inflows_count: 0,
    outflows_count: 0,
    synced_transactions: 0,
  });

  const [inflowHistory, setInflowHistory] = useState([]);
  const [outflowHistory, setOutflowHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [transferAmount, setTransferAmount] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingPagination, setIsUpdatingPagination] = useState(false);
  const initialLoadDoneRef = useRef(false);

  const LOAD_TIMEOUT_MS = 30000;

  // Pagination state for outflows
  const [outflowPagination, setOutflowPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  // Pagination state for inflows
  const [inflowPagination, setInflowPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });

  // Filter states for exports
  const [inflowFilters, setInflowFilters] = useState({
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    charges_min: '',
    charges_max: '',
    sort_by: 'date',
    sort_order: 'desc'
  });

  const [outflowFilters, setOutflowFilters] = useState({
    date_from: '',
    date_to: '',
    amount_min: '',
    amount_max: '',
    sort_by: 'date',
    sort_order: 'desc'
  });

  const fetchAccountStats = useCallback(async () => {
    try {
      const payInStatsResponse = await getPayInStats(partnerId, false, 1, 10);
      if (payInStatsResponse.success) {
        setAccountStats({
          balance: parseFloat(payInStatsResponse.account_balance) || 0,
          outflow: -parseFloat(payInStatsResponse.total_outflows) || 0,
          inflow: parseFloat(payInStatsResponse.total_inflows) || 0,
          inflows_count: payInStatsResponse.inflows_count || 0,
          outflows_count: payInStatsResponse.outflows_count || 0,
          synced_transactions: payInStatsResponse.synced_transactions || 0,
        });
      }
    } catch (error) {
      // Error handling without console logging
    }
  }, [partnerId]);

  const fetchOutflows = useCallback(async () => {
    try {
      setIsUpdatingPagination(true);

      const outflowResponse = await getPayInOutflows(
        partnerId,
        parseInt(outflowPagination.currentPage, 10),
        parseInt(outflowPagination.perPage, 10)
      );

      if (outflowResponse.success) {
        const processedOutflows = outflowResponse.outflows.map((txn) => ({
          id: txn.id,
          transaction_ref: txn.transaction_ref,
          status: txn.status,
          payer_name: txn.payer_name || "N/A",
          recipient_account_number: txn.recipient_account_number,
          recipient_account_name: txn.recipient_account_name,
          amount: parseFloat(txn.amount),
          date: txn.created_at,
        }));

        setOutflowHistory(processedOutflows);

        if (outflowResponse.pagination) {
          setOutflowPagination((prev) => ({
            ...prev,
            total: outflowResponse.pagination.total || 0,
            currentPage: parseInt(outflowResponse.pagination.current_page, 10) || prev.currentPage,
            perPage: parseInt(outflowResponse.pagination.per_page, 10) || prev.perPage,
          }));
        } else {
          setOutflowPagination((prev) => ({
            ...prev,
            total: outflowResponse.total || outflowResponse.outflows.length,
          }));
        }
      }
    } catch (error) {
      // Error handling without console logging
    } finally {
      setIsUpdatingPagination(false);
    }
  }, [outflowPagination.currentPage, outflowPagination.perPage, partnerId]);

  const fetchInflows = useCallback(async () => {
    try {
      setIsUpdatingPagination(true);

      const inflowResponse = await getPayInInflows(
        partnerId,
        parseInt(inflowPagination.currentPage, 10),
        parseInt(inflowPagination.perPage, 10)
      );

      if (inflowResponse.success) {
        
        const processedInflows = inflowResponse.inflows.map((txn) => {
            const cleanDate = txn.date ? txn.date.replace(/\.\d+Z$/, 'Z') : null;
      
          const processedTxn = {
              id: `${txn.transaction_ref}-${txn.date || txn.created_at}`,
              transactionRef: txn.transaction_ref,
              payerAccountName: txn.payer_name,
              payerAccountNumber: txn.payer_account_number,
              payerBank: txn.payer_bank,
              recipientAccountNumber: txn.recipient_account_number,
              amount: parseFloat(txn.amount),
              charges: txn.charges || txn.charge || txn.transaction_charges || 'N/A',
              date: cleanDate ? new Date(cleanDate).toLocaleDateString() : 'N/A',
              paidAt: txn.date,
              details: txn.details,
              initiationTranRef: txn.initiation_tran_ref,
              merchantId: txn.merchantId,
              sync: txn.sync,
            };
          
          return processedTxn;
        });

        setInflowHistory(processedInflows);

        if (inflowResponse.pagination) {
          
          setInflowPagination((prev) => ({
            ...prev,
            total: inflowResponse.pagination.total || 0,
            currentPage: parseInt(inflowResponse.pagination.current_page, 10) || prev.currentPage,
            perPage: parseInt(inflowResponse.pagination.per_page, 10) || prev.perPage,
          }));
          
        } else {
        setInflowPagination((prev) => ({
          ...prev,
          total: inflowResponse.total || inflowResponse.inflows.length,
        }));
        }
      } else {
        // Error handling without console logging
      }
    } catch (error) {
      // Error handling without console logging
    } finally {
      setIsUpdatingPagination(false);
    }
  }, [inflowPagination.currentPage, inflowPagination.perPage, partnerId]);

  const fetchAllData = useCallback(async () => {
    try {
      await Promise.all([
        fetchAccountStats(),
        fetchInflows(),
        fetchOutflows()
      ]);
    } catch (error) {
      // Error handling without console logging
    }
  }, [fetchAccountStats, fetchInflows, fetchOutflows]);

  const fetchInitialData = useCallback(async () => {
    if (!partnerId) {
      setIsLoading(false);
      setLoadError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timed out. The server may be slow or unreachable.")), LOAD_TIMEOUT_MS)
      );
      await Promise.race([fetchAllData(), timeoutPromise]);
    } catch (error) {
      setLoadError(error?.message || "Failed to load dashboard. Please try again.");
    } finally {
      setIsLoading(false);
      initialLoadDoneRef.current = true;
    }
  }, [fetchAllData, partnerId]);

  // Initial data load
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Handle pagination changes (skip on initial mount — fetchInitialData handles that)
  useEffect(() => {
    if (initialLoadDoneRef.current) {
      fetchInflows();
    }
  }, [fetchInflows, inflowPagination.currentPage, inflowPagination.perPage]);

  useEffect(() => {
    if (initialLoadDoneRef.current) {
      fetchOutflows();
    }
  }, [fetchOutflows, outflowPagination.currentPage, outflowPagination.perPage]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchAccountStats(),
        fetchInflows(),
        fetchOutflows()
      ]);
    } catch (error) {
      // Error handling without console logging
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchAccountStats, fetchInflows, fetchOutflows]);

  // Utility function to clean filters
  const cleanFilters = (filters) => {
    const cleaned = { ...filters };
    Object.keys(cleaned).forEach(key => {
      const value = cleaned[key];
      if (value === '' || value === null || value === undefined || value === 'undefined') {
        delete cleaned[key];
      }
    });
    
    // Ensure we have at least default sorting if no other filters
    if (Object.keys(cleaned).length === 0) {
      cleaned.sort_by = 'date';
      cleaned.sort_order = 'desc';
    }
    
    return cleaned;
  };

  const handleOutflowExport = async () => {
    try {
      const cleanFiltersData = cleanFilters(outflowFilters);
      return await exportPayInOutflows(partnerId, cleanFiltersData);
    } catch (error) {
      // Error handling without console logging
      return { success: false, message: 'Export failed. Please try again.' };
    }
  };

  const handleInflowExport = async () => {
    try {
      const cleanFiltersData = cleanFilters(inflowFilters);
      return await exportPayInInflows(partnerId, cleanFiltersData);
    } catch (error) {
      // Error handling without console logging
      return { success: false, message: 'Export failed. Please try again.' };
    }
  };

  const handleTransfer = async () => {
    if (isTestPartner) {
      showToast("error", "Fund transfers are not permitted for test accounts.", 3000);
      setTransferError("Fund transfers are not permitted for test accounts.");
      return;
    }

    if (!transferAmount) {
      setTransferError("Please fill in all fields");
      return;
    }

    if (parseFloat(transferAmount) > accountStats.balance) {
      setTransferError("Insufficient balance for this transfer");
      return;
    }

    setIsTransferring(true);
    setTransferError("");
    setTransferSuccess("");

    try {
      const transferData = {
        amount: parseFloat(transferAmount),
        payer_name: userFirstName,
      };

      const result = await transferToPayout(partnerId, transferData);
      
      if (result.success) {
        setTransferSuccess(`Transfer successful! Reference: ${result.transactionReference}`);
        setTransferAmount("");
        await fetchAllData();
      } else {
        const errorMessage = result.message || result.error?.message || "Transfer failed. Please try again.";
        setTransferError(errorMessage);
        if (result.error === "READ_ONLY_ACCESS") {
          showToast("error", errorMessage, 3000);
        }
      }
    } catch (error) {
      setTransferError("An error occurred during transfer. Please try again.");
    } finally {
      setIsTransferring(false);
    }
  };

  // Prepare data for the chart
  const prepareChartData = () => {
    const inflowData = {};
    const outflowData = {};

    inflowHistory.forEach((txn) => {
      const date = txn.date;
      if (!inflowData[date]) {
        inflowData[date] = 0;
      }
      inflowData[date] += txn.amount;
    });

    outflowHistory.forEach((txn) => {
      const date = txn.date;
      if (!outflowData[date]) {
        outflowData[date] = 0;
      }
      outflowData[date] += Math.abs(txn.amount);
    });

    const dates = [...new Set([...Object.keys(inflowData), ...Object.keys(outflowData)])].sort();

    const inflowValues = dates.map((date) => inflowData[date] || 0);
    const outflowValues = dates.map((date) => outflowData[date] || 0);

    return {
      labels: dates,
      datasets: [
        {
          label: "Inflow",
          data: inflowValues,
          borderColor: "rgba(75, 192, 192, 1)",
          backgroundColor: "rgba(75, 192, 192, 0.2)",
          fill: true,
        },
        {
          label: "Outflow",
          data: outflowValues,
          borderColor: "rgba(255, 99, 132, 1)",
          backgroundColor: "rgba(255, 99, 132, 0.2)",
          fill: true,
        },
      ],
    };
  };

  const chartData = prepareChartData();

  // Handle page change for inflows
  const handleInflowPageChange = (page) => {
    // Ensure page is a valid number
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) {
      return;
    }
    
    // Ensure pagination values are numbers
    const total = parseInt(inflowPagination.total, 10) || 0;
    const perPage = parseInt(inflowPagination.perPage, 10) || 10;
    
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    // Update pagination state
    setInflowPagination((prev) => ({
      ...prev,
      currentPage: validPage,
      total: total,
      perPage: perPage
    }));
  };

  // Handle page change for outflows
  const handleOutflowPageChange = (page) => {
    // Ensure page is a valid number
    const requestedPage = parseInt(page, 10);
    if (isNaN(requestedPage)) {
      return;
    }
    
    // Ensure pagination values are numbers
    const total = parseInt(outflowPagination.total, 10) || 0;
    const perPage = parseInt(outflowPagination.perPage, 10) || 10;
    
    const totalPages = Math.ceil(total / perPage);
    const validPage = Math.max(1, Math.min(requestedPage, totalPages));
    
    // Update pagination state
    setOutflowPagination((prev) => ({
      ...prev,
      currentPage: validPage,
      total: total,
      perPage: perPage
    }));
  };

  // Handle rows per page change for inflows
  const handleInflowRowsPerPageChange = (perPage) => {
    const newPerPage = parseInt(perPage, 10) || 10;
    setInflowPagination((prev) => ({ 
      ...prev, 
      perPage: newPerPage, 
      currentPage: 1 
    }));
  };

  // Handle rows per page change for outflows
  const handleOutflowRowsPerPageChange = (newPerPage) => {
    const parsedPerPage = parseInt(newPerPage, 10) || 10;
    setOutflowPagination(prev => ({ 
      ...prev, 
      perPage: parsedPerPage, 
      currentPage: 1 
    }));
  };

  if (loadError) {
    return (
      <section className="mt-20 min-h-screen">
        <div className="flex flex-col items-center justify-center py-20" style={{ color: theme?.colors?.text?.primary }}>
          <p className="text-red-500 mb-4 text-center max-w-md">{loadError}</p>
          <button
            onClick={fetchInitialData}
            className="px-6 py-2 rounded-lg font-medium transition-colors"
            style={{
              background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)",
              color: "#fff",
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section className="m-2 md:m-10 mt-24 p-2 md:p-10 min-h-screen flex items-center justify-center">
        <Loader message="Loading dashboard data..." />
      </section>
    );
  }

  return (
    <section className="mt-20 min-h-screen">
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 mt-8">
        <StatBox 
          title="Account Balance" 
          value={`₦${accountStats.balance.toLocaleString()}`} 
          theme={theme} 
          icon={<FaCreditCard color="#151821" />} 
          type="balance"
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          syncedCount={accountStats.synced_transactions}
        />
        
        <StatBox 
          title="Outflow" 
          value={`₦${Math.abs(accountStats.outflow).toLocaleString()}`} 
          theme={theme} 
          icon={<BiTrendingDown className="text-red-500" />} 
          isNegative 
          type="outflow"
          extra={`${(accountStats.outflows_count || 0).toLocaleString()} transactions`}
        />
        
        <StatBox 
          title="Inflow" 
          value={`₦${accountStats.inflow.toLocaleString()}`} 
          theme={theme} 
          icon={<BiTrendingUp className="text-green-500" />} 
          isPositive 
          type="inflow"
          extra={`${(accountStats.inflows_count || 0).toLocaleString()} transactions`}
        />
      </div>

      {/* Transfer Section */}
      <TransferToPayout 
        transferAmount={transferAmount}
        setTransferAmount={setTransferAmount}
        handleTransfer={handleTransfer}
        isTransferring={isTransferring}
        transferError={transferError}
        transferSuccess={transferSuccess}
        accountBalance={accountStats.balance}
        isTestPartner={isTestPartner}
      />

      <InflowVsOutFlowChart inflowHistory={inflowHistory} outflowHistory={outflowHistory} chartData={chartData} theme={theme} />
      
      {/* Inflow History Table */}
      <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Inflow History</h3>
        </div>
        
        {isUpdatingPagination ? (
          <Loader message="Loading inflow data..." />
        ) : inflowHistory.length === 0 ? (
          <NoData message="No inflow data available" />
        ) : (
          <TableSection 
            data={inflowHistory} 
            columns={inflowColumns} 
            theme={theme} 
            pagination={inflowPagination}
            onPageChange={handleInflowPageChange}
            onRowsPerPageChange={handleInflowRowsPerPageChange}
            onExport={handleInflowExport}
            showFilters={true}
            showExport={true}
            filters={inflowFilters}
            onFiltersChange={setInflowFilters}
            defaultFilters={{
              sort_by: 'date',
              sort_order: 'desc',
              charges_min: '',
              charges_max: ''
            }}
            sortFieldOptions={[
              { value: 'date', label: 'Date' },
              { value: 'amount', label: 'Amount' },
              { value: 'charges', label: 'Charges' },
              { value: 'created_at', label: 'Created At' }
            ]}
          />
        )}
        
      </div>
      
      {/* Outflow History Table */}
      <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Outflow History</h3>
        </div>
        {isUpdatingPagination ? (
          <Loader message="Loading outflow data..." />
        ) : outflowHistory.length === 0 ? (
          <NoData message="No outflow data available" />
        ) : (
          <TableSection 
            data={outflowHistory} 
            columns={outflowColumns} 
            theme={theme} 
            pagination={outflowPagination}
            onPageChange={handleOutflowPageChange}
            onRowsPerPageChange={handleOutflowRowsPerPageChange}
            onExport={handleOutflowExport}
            showFilters={true}
            showExport={true}
            filters={outflowFilters}
            onFiltersChange={setOutflowFilters}
            defaultFilters={{
              sort_by: 'date',
              sort_order: 'desc'
            }}
            sortFieldOptions={[
              { value: 'date', label: 'Date' },
              { value: 'amount', label: 'Amount' },
              { value: 'created_at', label: 'Created At' }
            ]}
          />
        )}
      </div>

      {/* Transfer to Payout Table */}
      {/* <div className="rounded-lg p-5 mt-8" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Transfer to Payout History</h3>
        </div>
        {isUpdatingPagination ? (
          <Loader message="Loading transfer to payout data..." />
        ) : transferToPayoutHistory.length === 0 ? (
          <NoData message="No transfer to payout data available" />
        ) : (
          <TableSection 
            data={transferToPayoutHistory} 
            columns={transferToPayoutColumns} 
            theme={theme} 
            pagination={transferToPayoutPagination}
            onPageChange={handleTransferToPayoutPageChange}
            onRowsPerPageChange={handleTransferToPayoutRowsPerPageChange}
            onExport={handleTransferToPayoutExport}
            showFilters={true}
            showExport={true}
            filters={transferToPayoutFilters}
            onFiltersChange={setTransferToPayoutFilters}
            defaultFilters={{
              sort_by: 'date',
              sort_order: 'desc'
            }}
            sortFieldOptions={[
              { value: 'date', label: 'Date' },
              { value: 'amount', label: 'Amount' },
              { value: 'created_at', label: 'Created At' }
            ]}
          />
        )}
      </div> */}

    </section>
  );
};

const inflowColumns = [
  { key: "transactionRef", label: "Transaction Reference" },
  { key: "payerAccountName", label: "Payment Initiator" },
  { key: "payerAccountNumber", label: "Payer Account" },
  { key: "payerBank", label: "Payer Bank" },
  { key: "recipientAccountNumber", label: "Recipient Account" },
  { 
    key: "amount", 
    label: "Amount",
    format: (value) => `₦${parseFloat(value).toLocaleString()}`
  },
  { key: "charges", label: "Charges" },
  { key: "date", label: "Date/Time" },
  { key: "initiationTranRef", label: "Initiation Ref" },
];

const outflowColumns = [
  { key: "transaction_ref", label: "Transaction ID" },
  { key: "status", label: "Transaction Status" },
  { key: "payer_name", label: "Payer Name" },
  { key: "recipient_account_number", label: "Destination Acct" },
  { key: "recipient_account_name", label: "Dest Acct Name" },
  { 
    key: "amount", 
    label: "Amount",
    format: (value) => `₦${parseFloat(value).toLocaleString(undefined, {minimumFractionDigits: 2})}`
  },
  { 
    key: "date", 
    label: "Date",
    format: (value) => new Date(value).toLocaleDateString()
  },
];

export default PayInDashboard;